/** Moneda de un retiro según la respuesta del API. */
export function monedaRetiro(retiro) {
  const moneda = retiro?.moneda ?? retiro?.Moneda;
  if (typeof moneda === "string" && moneda.trim()) {
    return moneda.trim().toUpperCase();
  }
  return "UYU";
}

/** Formatea cantidad/importe de retiro con la moneda explícita. */
export function formatImporteRetiro(cantidad, moneda = "UYU") {
  const num = Number(cantidad);
  if (Number.isNaN(num)) return "—";

  const formatted = num.toLocaleString("de-DE");
  const code = String(moneda || "UYU").trim().toUpperCase() || "UYU";

  if (code === "USD") {
    return `US$ ${formatted}`;
  }
  if (code === "UYU") {
    return `$ ${formatted} UYU`;
  }
  return `${formatted} ${code}`;
}

const ordenMoneda = (a, b) => {
  const priority = { UYU: 0, USD: 1 };
  const pa = priority[a] ?? 99;
  const pb = priority[b] ?? 99;
  if (pa !== pb) return pa - pb;
  return a.localeCompare(b);
};

/** Suma importes agrupados por moneda. */
export function sumarPorMoneda(items, options = {}) {
  const {
    cantidadKey = "cantidad",
    monedaKey = "moneda",
    getMoneda,
  } = options;

  if (!Array.isArray(items)) return {};

  return items.reduce((acc, item) => {
    const cantidad = Number(item?.[cantidadKey]);
    if (Number.isNaN(cantidad)) return acc;

    const moneda = getMoneda
      ? getMoneda(item)
      : item?.[monedaKey] || monedaRetiro(item);

    acc[moneda] = (acc[moneda] || 0) + cantidad;
    return acc;
  }, {});
}

/** Combina dos mapas de totales por moneda. */
export function mergeTotalesPorMoneda(a = {}, b = {}) {
  const result = { ...a };
  Object.entries(b).forEach(([moneda, total]) => {
    result[moneda] = (result[moneda] || 0) + total;
  });
  return result;
}

/** Formatea totales; con varias monedas muestra cada una por separado. */
export function formatTotalesPorMoneda(totalesPorMoneda, { separator = " / " } = {}) {
  const entries = Object.entries(totalesPorMoneda || {}).filter(([, total]) => total > 0);
  if (entries.length === 0) return "—";

  entries.sort(([a], [b]) => ordenMoneda(a, b));
  return entries
    .map(([moneda, total]) => formatImporteRetiro(total, moneda))
    .join(separator);
}

/** Promedio de importe por moneda (redondeado hacia arriba). */
export function promediosPorMoneda(items, options = {}) {
  const {
    cantidadKey = "cantidad",
    getMoneda = monedaRetiro,
  } = options;

  if (!Array.isArray(items)) return {};

  const acumulado = items.reduce((acc, item) => {
    const cantidad = Number(item?.[cantidadKey]);
    if (Number.isNaN(cantidad)) return acc;

    const moneda = getMoneda(item);
    if (!acc[moneda]) acc[moneda] = { sum: 0, count: 0 };
    acc[moneda].sum += cantidad;
    acc[moneda].count += 1;
    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(acumulado).map(([moneda, { sum, count }]) => [
      moneda,
      count > 0 ? Math.ceil(sum / count) : 0,
    ])
  );
}

/** Moneda predominante de una lista de retiros (primera encontrada). */
export function monedaDesdeRetiros(retiros) {
  if (!Array.isArray(retiros) || retiros.length === 0) return "UYU";
  return monedaRetiro(retiros[0]);
}

/** Actualiza totalesPorMoneda al agregar una línea de detalle. */
export function agregarLineaConTotal(grupo, linea) {
  grupo.detalle.push(linea);
  if (!grupo.totalesPorMoneda) grupo.totalesPorMoneda = {};
  const moneda = linea.moneda || monedaRetiro(linea);
  grupo.totalesPorMoneda[moneda] = (grupo.totalesPorMoneda[moneda] || 0) + linea.cantidad;
}

/** Crea totalesPorMoneda inicial para un grupo nuevo. */
export function totalesInicialesDesdeLinea(linea) {
  const moneda = linea.moneda || monedaRetiro(linea);
  return { [moneda]: linea.cantidad };
}
